import { ReactNode, createContext, useCallback } from 'react';
import { Permission, Permissions } from '../constants/permissionsConstants';
import { selectClusterIds, selectClustersById } from '../slices/clusters';
import { useAppSelector } from '../store/hooks';
import { hasPermission } from '../utils/permissions';

type Context = {
  satisfies: (params: {
    authorizations?: Permissions | Permissions[];
    clusterAuthorization?: Permission | Permission[];
    action?: Permission | Permission[];
  }) => boolean;
};

export const PermissionsContext = createContext<Context>({
  satisfies: () => false,
});

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const clusterIds = useAppSelector(selectClusterIds);
  const clustersById = useAppSelector(selectClustersById);

  const satisfies: Context['satisfies'] = useCallback(
    ({ authorizations, action, clusterAuthorization } = {}) => {
      if (clusterAuthorization) {
        if (Array.isArray(clusterAuthorization)) {
          return clusterIds.some((cID) =>
            clusterAuthorization.some((auth) => {
              const hasAuth = clustersById[cID]?.authorizations[auth];
              return Array.isArray(hasAuth) ? hasAuth.length : !!hasAuth;
            }),
          );
        }
        return clusterIds.some((cID) => {
          const authorizations = clustersById[cID]?.authorizations[clusterAuthorization];
          const typeOfAuthorizations = typeof authorizations;
          return (
            authorizations &&
            (typeOfAuthorizations === 'boolean' ||
              typeOfAuthorizations === 'string' ||
              (Array.isArray(authorizations) && authorizations.length > 0))
          );
        });
      }
      if (authorizations && Array.isArray(authorizations)) {
        return authorizations.reduce((isAuthorized, authorization) => {
          if (!action) {
            return false;
          }
          if (Array.isArray(action)) {
            return action.reduce((isAuthorized, a) => !!authorization[a] && isAuthorized, true);
          }
          return isAuthorized && authorization && !!authorization[action];
        }, true);
      }

      if (action && Array.isArray(action)) {
        if (!authorizations) {
          return false;
        }
        return action.reduce((isAuthorized, a) => !!authorizations[a] && isAuthorized, true);
      }
      return authorizations && action ? hasPermission({ authorizations }, action) : false;
    },
    [clusterIds, clustersById],
  );

  return <PermissionsContext.Provider value={{ satisfies }}>{children}</PermissionsContext.Provider>;
}
